import React, { useEffect } from "react";
import { useT } from "../i18n";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

// Only three text styles allowed on this page:
const H1_CLS = "text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-[#1D1D1F]";
const H2_CLS = "text-[22px] md:text-[24px] font-bold tracking-tight text-[#1D1D1F]";
const BODY_CLS = "text-[17px] leading-[1.6] text-[#1D1D1F]";

// Stable footer crumbs (module-level so React sees the same reference on every render)
const FOOTER_CRUMBS = [{ label: "Privacy Policy" }];

export default function Privacy() {
  const { t } = useT();

  // Always open the Privacy page scrolled to the very top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="privacy-page"
      className="min-h-screen bg-white text-[#1D1D1F]"
    >
      {/* Main site header (Apple-style, NOT sticky — scrolls away) */}
      <SiteHeader />

      {/* Section title bar — sticky under viewport top; aligned under PropHero logo */}
      <div className="sticky top-0 z-40 border-b border-black/5 bg-[#F5F5F7]/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 min-h-[51px] flex items-center text-[16px] leading-[20px] font-medium text-[#1D1D1F]" style={{ fontFamily: `"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif`, letterSpacing: "-0.01em" }}>
          Privacy Policy
        </div>
      </div>

      {/* Body */}
      <article className="max-w-[720px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <h1 data-testid="privacy-headline" className={H1_CLS}>
          {t("privacy.headline")}
        </h1>
        <p className={`mt-4 ${BODY_CLS}`}>
          {t("privacy.updatedLabel")} {t("privacy.updated")}
        </p>

        <div className={`mt-10 space-y-8 pb-12 ${BODY_CLS}`}>
          <section>
            <p>
              {t("privacy.intro")}
              <a
                href="https://prophero.io"
                className="text-[#023CFA] underline decoration-[#023CFA] underline-offset-4"
              >
                prophero.io
              </a>
              {t("privacy.intro2")}
            </p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.collect.h")}</h2>
            <p className="mt-3">{t("privacy.collect.p1")}</p>
            <ul className="mt-3 list-disc pl-5 space-y-2">
              <li>{t("privacy.collect.li1")}</li>
              <li>{t("privacy.collect.li2")}</li>
              <li>{t("privacy.collect.li3")}</li>
              <li>{t("privacy.collect.li4")}</li>
              <li>{t("privacy.collect.li5")}</li>
            </ul>
            <p className="mt-3">{t("privacy.collect.p2")}</p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.use.h")}</h2>
            <p className="mt-3">{t("privacy.use.p1")}</p>
            <ul className="mt-3 list-disc pl-5 space-y-2">
              <li>{t("privacy.use.li1")}</li>
              <li>{t("privacy.use.li2")}</li>
              <li>{t("privacy.use.li3")}</li>
              <li>{t("privacy.use.li4")}</li>
            </ul>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.nosell.h")}</h2>
            <p className="mt-3">{t("privacy.nosell.p")}</p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.retention.h")}</h2>
            <p className="mt-3">{t("privacy.retention.p")}</p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.choices.h")}</h2>
            <p className="mt-3">
              {t("privacy.choices.p1")}
              <a
                href="mailto:hello@prophero.io"
                className="text-[#023CFA] underline decoration-[#023CFA] underline-offset-4"
              >
                hello@prophero.io
              </a>
              {t("privacy.choices.p2")}
            </p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.updates.h")}</h2>
            <p className="mt-3">{t("privacy.updates.p")}</p>
          </section>

          <section>
            <h2 className={H2_CLS}>{t("privacy.contact.h")}</h2>
            <p className="mt-3">{t("privacy.contact.p")}</p>
            <ul className="mt-3 space-y-2">
              <li>
                <a
                  href="mailto:hello@prophero.io"
                  className="inline-flex items-center gap-1 text-[#023CFA] underline decoration-[#023CFA] underline-offset-4"
                >
                  <span>hello@prophero.io</span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+15613167327"
                  className="inline-flex items-center gap-1 text-[#023CFA] underline decoration-[#023CFA] underline-offset-4"
                >
                  <span>(561) 316-7327</span>
                </a>
              </li>
            </ul>
          </section>
        </div>
      </article>
      <SiteFooter crumbs={FOOTER_CRUMBS} />
    </main>
  );
}
